import type { ReportAlertFilter, ReportFilters } from "../../lib/api";

// The Reports tab keeps its filters in the URL, so a filtered report is a shareable link.
// A preset range is resolved to an explicit from/to once, when the URL is read: paging
// must not slide with the clock.

const HOUR_MS = 3_600_000;

export const REPORT_RANGES = { "24h": 24 * HOUR_MS, "7d": 168 * HOUR_MS, "30d": 720 * HOUR_MS };
export type ReportRange = keyof typeof REPORT_RANGES;
export const DEFAULT_REPORT_RANGE: ReportRange = "24h";

export const reportRange = (params: URLSearchParams): ReportRange | null => {
  if (params.get("from") && params.get("to")) return null;
  const range = params.get("range");
  return range && range in REPORT_RANGES ? (range as ReportRange) : DEFAULT_REPORT_RANGE;
};

const reportWindow = (params: URLSearchParams, now: number): { from: string; to: string } => {
  const range = reportRange(params);
  if (range === null) return { from: params.get("from") ?? "", to: params.get("to") ?? "" };
  return {
    from: new Date(now - REPORT_RANGES[range]).toISOString(),
    to: new Date(now).toISOString(),
  };
};

// The vision model's categories, verbatim from shobdo-agent prompts/ssm_v3_period.txt.
export const REPORT_CATEGORIES = ["dangerous", "illegal", "suspicious", "unusual", "none"] as const;

export const ALERT_FILTERS: readonly ReportAlertFilter[] = ["sent", "suppressed", "none"];

const severityParam = (value: string | null): number | null => {
  const n = value === null ? Number.NaN : Number(value);
  return Number.isInteger(n) && n >= 0 && n <= 5 ? n : null;
};

const alertParam = (value: string | null): ReportAlertFilter | null =>
  ALERT_FILTERS.find((a) => a === value) ?? null;

export const filtersFromParams = (params: URLSearchParams, now: number): ReportFilters => ({
  ...reportWindow(params, now),
  cameras: params.getAll("camera"),
  minSeverity: severityParam(params.get("min_severity")),
  alert: alertParam(params.get("alert")),
  category: params.get("category") || null,
  q: params.get("q") ?? "",
});
